
import React, { useState, useEffect, useRef } from "react";
import { Card } from "@/components/ui/card";

interface TalkingPersonAvatarProps {
  imageSrc: string;
  isSpeaking: boolean;
  message?: string;
  onSpeechEnd?: () => void;
}

const TalkingPersonAvatar: React.FC<TalkingPersonAvatarProps> = ({
  imageSrc,
  isSpeaking,
  message,
  onSpeechEnd
}) => {
  const [mouthOpen, setMouthOpen] = useState(false);
  const [mouthWidth, setMouthWidth] = useState(20); // Controls mouth width
  const speechSynthRef = useRef<SpeechSynthesisUtterance | null>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  
  // Simulate mouth movement when speaking with random variations
  useEffect(() => {
    if (!isSpeaking) {
      setMouthOpen(false);
      return;
    }
    
    // Create more natural speaking pattern with varying speeds and openings
    const interval = setInterval(() => {
      setMouthOpen(prev => !prev);
      // Randomly vary the mouth width for more natural effect
      setMouthWidth(Math.floor(Math.random() * 10) + 15);
    }, Math.floor(Math.random() * 100) + 100); // Random interval between 100-200ms
    
    return () => clearInterval(interval);
  }, [isSpeaking]);
  
  // Handle text-to-speech when message changes
  useEffect(() => {
    if (message && isSpeaking && window.speechSynthesis) {
      // Cancel any ongoing speech
      window.speechSynthesis.cancel();
      
      // Create new speech utterance
      const utterance = new SpeechSynthesisUtterance(message);
      utterance.rate = 1; // Speech rate
      utterance.pitch = 1; // Speech pitch
      
      // Get available voices and set a good one if available
      const voices = window.speechSynthesis.getVoices();
      const preferredVoice = voices.find(voice => 
        voice.name.includes('Google') || voice.name.includes('English')
      );
      
      if (preferredVoice) {
        utterance.voice = preferredVoice;
      }
      
      // Handle speech end
      utterance.onend = () => {
        if (onSpeechEnd) {
          onSpeechEnd();
        }
      };
      
      // Store reference and speak
      speechSynthRef.current = utterance;
      window.speechSynthesis.speak(utterance);
    }
    
    return () => {
      if (speechSynthRef.current) {
        window.speechSynthesis.cancel();
      }
    };
  }, [message, isSpeaking, onSpeechEnd]);
  
  return (
    <div className="relative w-full max-w-md mx-auto">
      <Card className="overflow-hidden rounded-xl shadow-lg">
        {/* Main image */}
        <div className="relative">
          <img 
            src={imageSrc} 
            alt="Avatar" 
            className="w-full h-auto object-cover"
          />
          
          {/* Mouth overlay - only visible when speaking */}
          {isSpeaking && (
            <div className="absolute bottom-[30%] left-1/2 transform -translate-x-1/2 z-10">
              <div 
                className={`bg-black rounded-b-full transition-all duration-75 ${mouthOpen ? 'h-6' : 'h-1'}`} 
                style={{ 
                  width: `${mouthWidth}px`,
                  transition: 'height 0.1s ease-in-out'
                }}
              />
            </div>
          )}
          
          {/* Speech indicator */}
          {isSpeaking && (
            <div className="absolute top-4 right-4 flex items-center gap-1 bg-primary/30 backdrop-blur-sm rounded-full px-3 py-1 text-sm animate-pulse">
              <span className={`h-2 w-2 rounded-full ${mouthOpen ? 'bg-primary' : 'bg-primary/50'}`}></span>
              <span className={`h-3 w-2 rounded-full ${!mouthOpen ? 'bg-primary' : 'bg-primary/50'}`}></span>
              <span className={`h-2 w-2 rounded-full ${mouthOpen ? 'bg-primary' : 'bg-primary/50'}`}></span>
              <span className="ml-1 text-primary-foreground">Speaking...</span>
            </div>
          )}
        </div>
      </Card>
      
      {/* Audio element for external audio if needed */}
      <audio ref={audioRef} className="hidden" />
      
      {/* Message bubble */}
      {message && (
        <div className="mt-4 bg-secondary p-4 rounded-lg relative">
          <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-0 h-0 border-l-8 border-r-8 border-b-8 border-l-transparent border-r-transparent border-b-secondary"></div>
          <p className="text-secondary-foreground">{message}</p>
        </div>
      )}
    </div>
  );
};

export default TalkingPersonAvatar;
